/**
 * fish-cart-gate.js — fish-only cart rules + the single checkout gate
 * (PondKing.Gate). Loaded sitewide from layout/theme.liquid.
 *
 * Owns:
 *  1. Add-to-cart interception: a cart may hold fish (product_type
 *     'Live Animals') or other products, never both. Conflicts get a
 *     "Clear cart & add" modal. The bulk-order fish table syncs its desired
 *     quantities through PondKing.Gate.syncFish (see fish-table.js).
 *  2. Checkout gating on #CartPageForm: capture-phase submit listener on
 *     `document` (guaranteed to run before theme.js's form-level handlers).
 *     Blocking reasons are registered, not hardcoded:
 *       PondKing.Gate.addBlocker(id, fn)    fn(ctx) -> null | {message}
 *       PondKing.Gate.addValidator(id, fn)  fn(ctx) -> Promise<null|{message}>
 *     ctx.phase is 'passive' (UI state sync) or 'checkout' (real submit).
 *     Defaults: fish-mixed-cart, fish-sold-out-line (from #FishCartStatus).
 *     fish-pickup.js registers the pickup-slot blocker + validator.
 *  3. Defensive cart-drawer coverage (drawer is inactive today): fish carts
 *     are sent to /cart where the full slot UI lives.
 *
 * This gate is UX enforcement; Shopify checkout (tracked inventory, no
 * oversell) is the hard backstop.
 */
(function () {
  'use strict';

  window.PondKing = window.PondKing || {};
  if (window.PondKing.Gate) return;

  var cfg = window.PondKingFish || {};
  var strings = cfg.strings || {};
  var FISH_TYPE = cfg.fishType || 'Live Animals';

  // Page-load composition from Liquid; re-synced from /cart.js after every
  // interception. Only used for the fast path — decisions re-fetch live.
  var cartState = {
    hasFish: !!cfg.cartHasFish,
    hasNonfish: !!cfg.cartHasNonfish
  };

  function syncState(cart) {
    var hasFish = false;
    var hasNonfish = false;
    (cart.items || []).forEach(function (item) {
      if (item.product_type === FISH_TYPE) hasFish = true;
      else hasNonfish = true;
    });
    cartState.hasFish = hasFish;
    cartState.hasNonfish = hasNonfish;
    return cart;
  }

  function fetchCart() {
    return fetch('/cart.js?_fts=' + Date.now(), { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(syncState);
  }

  function statusEl() {
    return document.getElementById('FishCartStatus');
  }

  function clearSlotAttributes() {
    var attributes = { pickup_slot_handle: '' };
    attributes['Pickup Slot'] = '';
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ attributes: attributes })
    }).then(function (r) {
      if (!r.ok) throw new Error('cart attribute clear ' + r.status);
      return r;
    });
  }

  // Wipe every fish attribute (pickup slot + drive-time attestation + refund
  // agreement + grass carp permit) — used when a cart stops being a fish cart.
  function clearFishAttributes() {
    var cleared = { pickup_slot_handle: '', grass_carp_permit_url: '' };
    cleared['Pickup Slot'] = '';
    cleared['Drive Time Confirmed'] = '';
    cleared['Reservation Policy Agreed'] = '';
    cleared['Stocking In Texas'] = '';
    cleared['Grass Carp Permit'] = '';
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ attributes: cleared })
    });
  }

  // A cart that stopped containing fish (fish removed, then boats added) must
  // not carry fish attributes onto a non-fish order — the sync service would claim slot
  // capacity, and a drive-time confirmation would be meaningless. Swept on
  // cart-page loads.
  function sweepStaleSlotAttributes() {
    if (!document.getElementById('CartPageForm')) return;
    if (cartHasFishNow()) return;
    fetch('/cart.js?_fts=' + Date.now(), { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var attrs = cart.attributes || {};
        if (attrs.pickup_slot_handle || attrs['Pickup Slot'] || attrs['Drive Time Confirmed'] ||
            attrs['Reservation Policy Agreed'] || attrs['Stocking In Texas'] ||
            attrs['Grass Carp Permit'] || attrs.grass_carp_permit_url) {
          return clearFishAttributes();
        }
      })
      .catch(function () { /* retried on next cart-page visit */ });
  }

  // Cart page: when the last fish is removed via AJAX (no reload), the
  // required tick boxes rendered at load time linger inside the form — a
  // ticked one would natively re-post attributes[Drive Time Confirmed]=Yes
  // (or [Reservation Policy Agreed]=Yes) on a non-fish checkout. Hide + untick
  // them and wipe the attributes as soon as the freshly polled status says
  // the fish are gone.
  var FISH_TICKBOXES = [
    { box: '[data-fish-attest]', wrap: '[data-fish-attest-wrap]' },
    { box: '[data-fish-agree]', wrap: '[data-fish-agree-wrap]' }
  ];

  function reconcileAttestation() {
    var el = statusEl();
    if (!el || el.getAttribute('data-cart-has-fish') !== 'false') return;
    var needsClear = false;
    for (var i = 0; i < FISH_TICKBOXES.length; i++) {
      var box = document.querySelector(FISH_TICKBOXES[i].box);
      if (!box) continue;
      var wrap = box.closest(FISH_TICKBOXES[i].wrap);
      if (wrap && !wrap.classList.contains('hide')) wrap.classList.add('hide');
      if (box.checked) {
        box.checked = false;
        needsClear = true;
      }
    }
    if (needsClear) clearFishAttributes().catch(function () { /* swept on next load */ });
  }

  function cartHasFishNow() {
    var el = statusEl();
    if (el) return el.getAttribute('data-cart-has-fish') === 'true';
    return cartState.hasFish;
  }

  /* ---------------- gate registry ---------------- */

  var blockers = {};
  var validators = {};

  function runBlockers(ctx) {
    for (var id in blockers) {
      if (!Object.prototype.hasOwnProperty.call(blockers, id)) continue;
      var result;
      try { result = blockers[id](ctx); } catch (err) { result = null; }
      if (result) { result.id = id; return result; }
    }
    return null;
  }

  function runValidators(ctx) {
    var ids = Object.keys(validators);
    var chain = Promise.resolve(null);
    ids.forEach(function (id) {
      chain = chain.then(function (prev) {
        if (prev) return prev; // first failure wins
        return validators[id](ctx);
      });
    });
    return chain;
  }

  function messageRegion() {
    return document.querySelector('[data-fish-gate-messages]');
  }

  function showMessage(result) {
    var region = messageRegion();
    if (!region) {
      if (result && result.message) window.alert(stripHtml(result.message));
      return;
    }
    region.innerHTML = result
      ? '<div class="fish-gate__message" role="alert">' + result.message + '</div>'
      : '';
    if (result) {
      region.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  function stripHtml(html) {
    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || '';
  }

  // Gate messages render via innerHTML (locale *_html strings carry markup);
  // merchant-editable settings text must be escaped before joining them.
  function escapeHtml(text) {
    return String(text).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  function evaluate() {
    // Passive pass: style the checkout button only. The loud role="alert"
    // message renders on actual checkout attempts (and the fish-cart-status
    // banners already explain mixed/sold-out states) — no red error before
    // the customer has done anything.
    var form = document.getElementById('CartPageForm');
    if (!form) return;
    var result = cartHasFishNow() ? runBlockers({ phase: 'passive' }) : null;
    if (!result) showMessage(null); // clear a stale checkout-attempt message
    var buttons = form.querySelectorAll('.cart__checkout');
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].classList.toggle('fish-gate--blocked', !!result);
      buttons[i].setAttribute('aria-disabled', result ? 'true' : 'false');
    }
  }

  /* ---------------- checkout interception ---------------- */

  function onCheckoutSubmit(evt, form) {
    if (form.dataset.fishVerified === '1') {
      delete form.dataset.fishVerified;
      return; // pass through to the theme's own handlers (terms gate etc.)
    }
    if (!cartHasFishNow()) return; // non-fish carts: untouched

    evt.preventDefault();
    evt.stopPropagation();

    var blocked = runBlockers({ phase: 'checkout' });
    if (blocked) { showMessage(blocked); return; }

    runValidators({ phase: 'checkout' })
      .then(function (failed) {
        if (failed) { showMessage(failed); return; }
        showMessage(null);
        form.dataset.fishVerified = '1';
        var btn = form.querySelector('[name="checkout"]');
        // Re-enter the native submit path so theme.js's handlers still run.
        if (btn) btn.click();
        else form.submit();
      })
      .catch(function () {
        // Fail CLOSED — never let a fish cart through unvalidated.
        showMessage({ message: strings.slotSaveFailed || 'Unable to verify your cart. Please try again.' });
      });
  }

  /* ---------------- add-to-cart interception ---------------- */

  function isFishForm(form) {
    return form.getAttribute('data-fish') === 'true' || !!form.closest('[data-fish="true"]');
  }

  function onAddSubmit(evt, form) {
    var fishAdd = isFishForm(form);
    // Fast path: non-fish add with no fish in the cart — the entire existing
    // store keeps its current behavior, zero extra requests.
    if (!fishAdd && !cartState.hasFish) return;

    evt.preventDefault();
    evt.stopPropagation();

    if (form.dataset.fishAddBusy === '1') return; // double-click guard
    form.dataset.fishAddBusy = '1';

    fetchCart()
      .then(function () {
        var conflict = (fishAdd && cartState.hasNonfish) || (!fishAdd && cartState.hasFish);
        if (!conflict) { addViaAjax(form); return; }
        showMixModal(fishAdd, {
          confirm: function () { addViaAjax(form); },
          cancel: function () { delete form.dataset.fishAddBusy; },
          error: function () {
            delete form.dataset.fishAddBusy;
            formError(form, null);
          }
        });
      })
      .catch(function () {
        // Cart composition unknown (offline?) — let the add proceed; the cart
        // page banners + checkout gate backstop any mixing.
        addViaAjax(form);
      });
  }

  function formError(form, message) {
    var node = form.querySelector('[data-form-error]');
    if (node) {
      node.textContent = stripHtml(message || strings.addError || 'Unable to add to cart.');
      node.classList.remove('hide');
    } else {
      window.alert(stripHtml(message || strings.addError || 'Unable to add to cart.'));
    }
  }

  function addViaAjax(form) {
    var btn = form.querySelector('[type="submit"], [name="add"]');
    if (btn) btn.classList.add('btn--loading');
    var errNode = form.querySelector('[data-form-error]');
    if (errNode) errNode.classList.add('hide');
    var navigating = false;

    var routes = (window.theme && window.theme.routes) || {};
    fetch(routes.cartAdd || '/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams(new FormData(form)).toString()
    })
      .then(function (r) {
        return r.json().then(function (data) { return { ok: r.ok, data: data }; });
      })
      .then(function (result) {
        if (!result.ok || result.data.status) {
          formError(form, result.data.description || result.data.message);
          if (window.PondKing.FishRefresh) window.PondKing.FishRefresh.refreshNow();
          return;
        }
        // Same event theme.js dispatches, for any listeners (drawer mode).
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: { product: result.data, addToCartBtn: btn }
        }));
        // With cart_type "page" this store's native add flow lands on the cart
        // page (theme.js only AJAXes product forms in drawer mode) — match it.
        navigating = true;
        window.location.href = routes.cartPage || '/cart';
      })
      .catch(function () {
        formError(form, null);
      })
      .then(function () {
        if (navigating) return; // keep the button locked until the page turns
        delete form.dataset.fishAddBusy;
        if (btn) btn.classList.remove('btn--loading');
      });
  }

  /* ---------------- cart sync (bulk-order fish table) ---------------- */

  /**
   * Apply a computed plan: absolute quantity updates for fish lines already
   * in the cart (one /cart/update.js call — also removes lines set to 0),
   * then a batch /cart/add.js for new variants. Success navigates to the cart
   * page, matching the store's native add flow.
   *
   * Shopify is NOT all-or-nothing here: a line over its sellable quantity is
   * clamped (422 "Only 60 items were added…") and the other lines still land.
   * On any failure the touched lines are rolled back to plan.before so the
   * cart matches what the customer last saw, and the error names each short
   * fish with the quantity Shopify would actually take.
   */
  function applyPlan(plan, cb) {
    cb = cb || {};
    var routes = (window.theme && window.theme.routes) || {};
    var jsonHeaders = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest'
    };

    function post(url, payload) {
      return fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: jsonHeaders,
        body: JSON.stringify(payload)
      }).then(function (r) {
        return r.json().catch(function () { return {}; }).then(function (data) {
          if (!r.ok || data.status) {
            var err = new Error('cart ' + url + ' ' + r.status);
            err.cartDescription = stripHtml(data.description || data.message || '');
            throw err;
          }
          return data;
        });
      });
    }

    var step = Promise.resolve();

    if (plan.updates && Object.keys(plan.updates).length) {
      step = step.then(function () {
        return post('/cart/update.js', { updates: plan.updates });
      });
    }

    if (plan.additions && plan.additions.length) {
      step = step.then(function () {
        return post(routes.cartAdd || '/cart/add.js', { items: plan.additions });
      });
    }

    step
      .then(function () {
        if (cb.success) cb.success();
        if (cb.redirect === false) return; // caller owns navigation
        window.location.href = cb.redirect || routes.cartPage || '/cart';
      })
      .catch(function (err) {
        return rollbackPlan(plan).then(function (shortMessage) {
          // Refresh counts so the table shows why (stock likely moved).
          if (window.PondKing.FishRefresh) window.PondKing.FishRefresh.refreshNow();
          if (cb.error) {
            cb.error(shortMessage || (err && err.cartDescription) || strings.addError || 'Unable to update the cart.');
          }
        });
      });
  }

  /**
   * After a failed applyPlan: read the cart to see what Shopify actually
   * kept, build a "not enough available" message for every line that came up
   * short, then put the touched lines back to their pre-plan quantities.
   * Resolves the message, or null when no line was short (or the cart
   * couldn't be read) — the caller falls back to Shopify's own text.
   */
  function rollbackPlan(plan) {
    var want = {};
    var before = plan.before || {};
    for (var uid in plan.updates || {}) {
      if (Object.prototype.hasOwnProperty.call(plan.updates, uid)) want[uid] = plan.updates[uid];
    }
    (plan.additions || []).forEach(function (a) { want[String(a.id)] = a.quantity; });

    return fetchCart()
      .then(function (cart) {
        var actual = {};
        var titles = {};
        (cart.items || []).forEach(function (item) {
          var id = String(item.variant_id);
          actual[id] = (actual[id] || 0) + item.quantity;
          titles[id] = item.title;
        });
        var short = [];
        var restore = {};
        for (var id in want) {
          if (!Object.prototype.hasOwnProperty.call(want, id)) continue;
          var have = actual[id] || 0;
          if (have < want[id] && titles[id]) {
            short.push((strings.shortStockLine || '[title] (only [count] available)')
              .replace('[title]', titles[id])
              .replace('[count]', String(have)));
          }
          if (have !== (before[id] || 0)) restore[id] = before[id] || 0;
        }
        var undo = Object.keys(restore).length
          ? fetch('/cart/update.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ updates: restore })
          }).then(fetchCart)
          : Promise.resolve();
        return undo
          .catch(function () { /* best effort — the cart page shows the truth */ })
          .then(function () {
            if (!short.length) return null;
            return (strings.shortStock ||
              'We can’t reserve that many online right now: [lines]. Nothing was added — lower those quantities or call the office.')
              .replace('[lines]', short.join('; '));
          });
      })
      .catch(function () { return null; });
  }

  /** Diff desired fish quantities against the cart's current fish lines. */
  function buildPlan(desired, cart) {
    var inCart = {};
    (cart.items || []).forEach(function (item) {
      if (item.product_type === FISH_TYPE) inCart[String(item.variant_id)] = item.quantity;
    });
    var updates = {};
    var additions = [];
    var before = {}; // pre-plan quantities of touched lines, for rollback
    for (var id in desired) {
      if (!Object.prototype.hasOwnProperty.call(desired, id)) continue;
      var want = Math.max(0, parseInt(desired[id], 10) || 0);
      var have = inCart[id] || 0;
      if (want === have) continue;
      before[id] = have;
      if (have > 0) updates[id] = want;
      else if (want > 0) additions.push({ id: parseInt(id, 10), quantity: want });
    }
    return { updates: updates, additions: additions, before: before };
  }

  /**
   * Sync the cart's fish lines to `desired` ({variantId: quantity}, absolute —
   * 0 removes a carted line; cart fish absent from `desired` are untouched),
   * with the same fish-only rules as single adds: a cart holding other
   * products gets the clear-cart modal first, then everything desired is a
   * plain addition. Used by assets/fish-table.js.
   * cb: { success(), error(message), cancel(), redirect } — success navigates
   * to cb.redirect (default the cart page; the fish page passes /checkout).
   */
  function syncFish(desired, cb) {
    cb = cb || {};
    desired = desired || {};
    var buying = false;
    for (var did in desired) {
      if (Object.prototype.hasOwnProperty.call(desired, did) &&
          (parseInt(desired[did], 10) || 0) > 0) { buying = true; break; }
    }
    fetchCart()
      .then(function (cart) {
        // Removals-only syncs never need the clear-cart modal: dropping fish
        // lines from a mixed cart is safe and touches nothing else — clearing
        // the whole cart for it would wipe the customer's other items.
        if (!cartState.hasNonfish || !buying) {
          applyPlan(buildPlan(desired, cart), cb);
          return;
        }
        showMixModal(true, {
          confirm: function () {
            // The cart was just cleared; everything desired becomes an add.
            // If that add then fails (all-or-nothing 422), say so explicitly —
            // the customer's old items are gone.
            var additions = [];
            for (var id in desired) {
              if (!Object.prototype.hasOwnProperty.call(desired, id)) continue;
              var want = Math.max(0, parseInt(desired[id], 10) || 0);
              if (want > 0) additions.push({ id: parseInt(id, 10), quantity: want });
            }
            applyPlan({ updates: {}, additions: additions }, {
              success: cb.success,
              cancel: cb.cancel,
              redirect: cb.redirect,
              error: function (message) {
                if (cb.error) {
                  cb.error((strings.clearedNotAdded || 'Your cart was cleared, but the fish could not be added: ') + message);
                }
              }
            });
          },
          cancel: function () { if (cb.cancel) cb.cancel(); },
          error: function (cleared) {
            if (!cb.error) return;
            // cleared=true → the cart was emptied but the flow aborted (e.g.
            // the pickup-slot re-save failed) — the customer must know their
            // old items are gone AND nothing was ordered.
            if (cleared) {
              cb.error((strings.clearedNotAdded || 'Your cart was cleared, but the fish could not be added: ') +
                (strings.slotSaveFailed || 'Please try again.'));
            } else {
              cb.error(strings.addError || 'Unable to update the cart.');
            }
          }
        });
      })
      .catch(function () {
        // Composition unknown (offline?) — do NOT blind-apply: without the
        // cart we can't diff, and re-adding would double carted quantities.
        if (cb.error) cb.error(strings.addError || 'Unable to update the cart.');
      });
  }

  /* ---------------- mixed-cart modal ---------------- */

  var modalEl = null;
  var modalCancel = null;
  var modalBusy = false; // "Clear cart & add" committed and in flight

  function closeModal() {
    if (modalEl && modalEl.parentNode) modalEl.parentNode.removeChild(modalEl);
    modalEl = null;
    document.removeEventListener('keydown', onModalKeydown);
  }

  // Cancel path (Cancel button, overlay click, Escape): releases whatever
  // state the caller latched (form busy flag, table button loading). Ignored
  // once the clear-cart commit is in flight — that operation can't be undone.
  function cancelModal() {
    if (modalBusy) return;
    var cb = modalCancel;
    modalCancel = null;
    closeModal();
    if (cb) cb();
  }

  function onModalKeydown(evt) {
    if (evt.key === 'Escape') cancelModal();
  }

  /**
   * "Live fish are sold separately" modal.
   * handlers.confirm() runs AFTER the cart has been cleared (line items AND
   * pickup-slot attributes) — the caller just re-issues its add.
   * handlers.cancel() runs on any dismissal; handlers.error() on clear failure.
   * Exactly ONE of confirm/cancel/error fires per showing.
   */
  function showMixModal(fishAdd, handlers) {
    if (modalBusy) {
      // A previous clear-cart commit is mid-flight — don't hijack it; treat
      // the new request as cancelled so its caller unlatches.
      if (handlers.cancel) handlers.cancel();
      return;
    }
    cancelModal(); // an already-open modal loses: fire ITS cancel, free its latch
    modalCancel = handlers.cancel || null;
    modalEl = document.createElement('div');
    modalEl.setAttribute('role', 'dialog');
    modalEl.setAttribute('aria-modal', 'true');
    modalEl.style.cssText =
      'position:fixed;inset:0;z-index:100000;display:flex;align-items:center;' +
      'justify-content:center;background:rgba(0,0,0,0.55);padding:20px;';

    var box = document.createElement('div');
    box.style.cssText =
      'background:#fff;color:#1c1d1d;max-width:420px;width:100%;border-radius:6px;' +
      'padding:24px;box-shadow:0 10px 40px rgba(0,0,0,0.3);text-align:center;';

    var title = document.createElement('p');
    title.style.cssText = 'font-weight:700;font-size:1.15em;margin:0 0 10px;';
    title.textContent = strings.modalTitle || 'Live fish are sold separately';

    var body = document.createElement('p');
    body.style.cssText = 'margin:0 0 18px;';
    body.textContent = fishAdd
      ? (strings.modalFish || 'Your cart contains other products. Live fish must be purchased on their own pickup-only order.')
      : (strings.modalNonfish || 'Your cart contains live fish. Fish must be checked out on their own before adding other products.');

    var actions = document.createElement('div');
    actions.style.cssText = 'display:flex;gap:10px;justify-content:center;flex-wrap:wrap;';

    var clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'btn';
    clearBtn.textContent = strings.clearAndAdd || 'Clear cart & add';

    var cancelBtn = document.createElement('button');
    cancelBtn.type = 'button';
    cancelBtn.className = 'btn btn--secondary';
    cancelBtn.textContent = strings.cancel || 'Cancel';
    cancelBtn.addEventListener('click', cancelModal);

    clearBtn.addEventListener('click', function () {
      if (modalBusy) return; // double-click guard
      modalBusy = true;
      clearBtn.disabled = true;
      cancelBtn.disabled = true;
      clearBtn.classList.add('btn--loading');
      var cleared = false; // lets handlers.error say whether the cart is gone
      fetch('/cart/clear.js', { method: 'POST', credentials: 'same-origin' })
        .then(function (r) {
          // fetch resolves on 429/5xx — confirming with a NOT-cleared cart
          // would create exactly the mixed cart this modal exists to prevent.
          if (!r.ok) throw new Error('cart clear ' + r.status);
          cleared = true;
          // /cart/clear.js empties line items but NOT cart attributes — wipe
          // the pickup-slot attributes too, or a later non-fish order would
          // carry a stale slot and the sync service would claim capacity for it. The cart
          // IS cleared at this point, so a failure here must not strand the
          // flow: the fish path re-sets the attributes when a slot is picked,
          // and sweepStaleSlotAttributes + the sync service's slot-attribute check
          // backstop the rest.
          return clearSlotAttributes().catch(function () { /* non-fatal */ });
        })
        .then(function () {
          // A slot picked moments earlier (fish page picker) must survive the
          // wipe — re-save the currently checked radio's attributes. A FAILED
          // re-save must abort: proceeding would place a fish order with no
          // pickup slot while telling the customer it's confirmed.
          var fishPickup = window.PondKing.FishPickup;
          if (fishPickup && fishPickup.reassert) {
            return fishPickup.reassert().then(function (res) {
              if (res && res.needed && !res.ok) throw new Error('slot reassert failed');
            });
          }
        })
        .then(function () {
          cartState.hasFish = false;
          cartState.hasNonfish = false;
          modalBusy = false;
          modalCancel = null; // confirmed — don't fire the cancel callback
          closeModal();
          handlers.confirm();
        })
        .catch(function () {
          modalBusy = false;
          modalCancel = null;
          closeModal();
          (handlers.error || handlers.cancel || function () {})(cleared);
        });
    });

    actions.appendChild(clearBtn);
    actions.appendChild(cancelBtn);
    box.appendChild(title);
    box.appendChild(body);
    box.appendChild(actions);
    modalEl.appendChild(box);
    modalEl.addEventListener('click', function (evt) {
      if (evt.target === modalEl) cancelModal();
    });
    document.addEventListener('keydown', onModalKeydown);
    document.body.appendChild(modalEl);
    cancelBtn.focus();
  }

  /* ---------------- listeners ---------------- */

  document.addEventListener('submit', function (evt) {
    var form = evt.target;
    if (!form || form.tagName !== 'FORM') return;
    if (form.id === 'CartPageForm') { onCheckoutSubmit(evt, form); return; }
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') !== -1) onAddSubmit(evt, form);
  }, true);

  // Cart drawer (inactive with cart_type=page, kept as cheap defense): send
  // fish carts to the cart page, where the slot picker and banners live.
  document.addEventListener('click', function (evt) {
    var btn = evt.target.closest ? evt.target.closest('[name="checkout"]') : null;
    if (!btn) return;
    var pageForm = btn.closest('#CartPageForm');
    if (pageForm) return; // handled by the submit listener
    var drawerVerified = btn.getAttribute('data-fish-drawer-checked') === '1';
    if (drawerVerified) return;
    evt.preventDefault();
    evt.stopPropagation();
    fetchCart().then(function () {
      if (cartState.hasFish) {
        window.location.href = '/cart#PickupSlotPicker';
        return;
      }
      btn.setAttribute('data-fish-drawer-checked', '1');
      btn.click();
      btn.removeAttribute('data-fish-drawer-checked');
    }).catch(function () {
      window.location.href = '/cart';
    });
  }, true);

  document.addEventListener('fish:refreshed', function () {
    reconcileAttestation();
    evaluate();
  });

  window.PondKing.Gate = {
    addBlocker: function (id, fn) { blockers[id] = fn; evaluate(); },
    removeBlocker: function (id) { delete blockers[id]; evaluate(); },
    addValidator: function (id, fn) { validators[id] = fn; },
    removeValidator: function (id) { delete validators[id]; },
    syncFish: syncFish,
    evaluate: evaluate,
    fetchCart: fetchCart
  };

  /* ---------------- default blockers (cart page) ---------------- */

  window.PondKing.Gate.addBlocker('fish-mixed-cart', function () {
    var el = statusEl();
    if (el && el.getAttribute('data-cart-mixed') === 'true') {
      return { message: strings.mixedBlocked || 'Live fish must be purchased separately. Remove the other items to continue.' };
    }
    return null;
  });

  window.PondKing.Gate.addBlocker('fish-sold-out-line', function () {
    var el = statusEl();
    if (el &&
        el.getAttribute('data-fish-blocked') === 'true' &&
        el.getAttribute('data-cart-mixed') !== 'true') {
      return { message: strings.stockBlocked || 'Some fish in your cart are no longer available in that quantity. Please adjust your cart.' };
    }
    return null;
  });

  // Online fish orders are capped (Theme settings → Live Fish Ordering) —
  // larger orders are scheduled deliveries arranged by phone.
  function overLimitMessage() {
    var el = statusEl();
    var merchant = el && el.getAttribute('data-fish-limit-message');
    // getAttribute returns DECODED text — escape before innerHTML rendering.
    if (merchant) return escapeHtml(merchant);
    return strings.overLimit || 'This order is over our online limit — please call the office to schedule a delivery.';
  }

  window.PondKing.Gate.addBlocker('fish-over-limit', function () {
    var el = statusEl();
    if (el && el.getAttribute('data-fish-over-limit') === 'true') {
      return { message: overLimitMessage() };
    }
    return null;
  });

  // The blocker above reads the RENDER-TIME attribute, which lags AJAX
  // quantity edits until the next poll — "bump quantity, click checkout"
  // must not slip past the cap. This validator re-checks against the live
  // cart at the moment of submission.
  window.PondKing.Gate.addValidator('fish-over-limit-fresh', function () {
    var el = statusEl();
    var max = el ? (parseInt(el.getAttribute('data-fish-max-total'), 10) || 0) : 0;
    if (!max) return Promise.resolve(null);
    return fetch('/cart.js?_fts=' + Date.now(), { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var subtotal = 0;
        (cart.items || []).forEach(function (item) {
          if (item.product_type === FISH_TYPE) subtotal += item.final_line_price || 0;
        });
        return subtotal >= max ? { message: overLimitMessage() } : null;
      });
  });

  // Required drive-time confirmation (fish-attestation snippet renders the
  // checkbox on the fish page and inside the cart form).
  window.PondKing.Gate.addBlocker('fish-drive-attest', function () {
    var box = document.querySelector('[data-fish-attest]');
    if (box && !box.checked) {
      return { message: strings.attestRequired || 'Please tick the drive-time confirmation before checking out.' };
    }
    return null;
  });

  // Record the confirmation on the cart (→ order note attribute). The DOM
  // checkbox is the gating source of truth; the attribute is the record and
  // the no-JS fallback posts it natively from the cart form.
  document.addEventListener('change', function (evt) {
    var box = evt.target;
    if (!box || !box.hasAttribute || !box.hasAttribute('data-fish-attest')) return;
    box.dataset.fishAttestTouched = '1'; // user intent beats any later cart-state sync
    var attributes = {};
    attributes['Drive Time Confirmed'] = box.checked ? (box.value || 'Yes') : '';
    fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ attributes: attributes })
    }).catch(function () { /* best-effort record; the checkbox still gates */ });
    evaluate();
  });

  // Required reservation-policy acknowledgement (fish-agreement snippet
  // renders the policy + checkbox on the fish page and inside the cart form).
  // Purely DOM-driven: no checkbox rendered (copy cleared) means nothing to gate.
  window.PondKing.Gate.addBlocker('fish-refund-agreement', function () {
    var box = document.querySelector('[data-fish-agree]');
    if (box && !box.checked) {
      return { message: strings.agreementRequired || 'Please tick the box to acknowledge the Live Fish Reservation & Cancellation Policy before checking out.' };
    }
    return null;
  });

  // Record the acknowledgement on the cart (→ order note attribute), same contract
  // as the drive-time confirmation: the DOM checkbox gates, the attribute is
  // the record, and the no-JS cart form posts it natively.
  document.addEventListener('change', function (evt) {
    var box = evt.target;
    if (!box || !box.hasAttribute || !box.hasAttribute('data-fish-agree')) return;
    box.dataset.fishAgreeTouched = '1'; // user intent beats any later cart-state sync
    var attributes = {};
    attributes['Reservation Policy Agreed'] = box.checked ? (box.value || 'Yes') : '';
    fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ attributes: attributes })
    }).catch(function () { /* best-effort record; the checkbox still gates */ });
    evaluate();
  });

  function init() {
    evaluate();
    sweepStaleSlotAttributes();
  }

  // bfcache back-nav restores the page with mid-flight state frozen in the
  // DOM: busy latches and loading buttons from an add that already navigated
  // away. Release them or the buttons stay dead.
  window.addEventListener('pageshow', function (evt) {
    if (!evt.persisted) return;
    var forms = document.querySelectorAll('form[data-fish-add-busy]');
    for (var i = 0; i < forms.length; i++) {
      delete forms[i].dataset.fishAddBusy;
      var btn = forms[i].querySelector('.btn--loading');
      if (btn) btn.classList.remove('btn--loading');
    }
    fetchCart().then(evaluate).catch(function () { /* next interaction re-syncs */ });
  });

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
})();
